import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { searchAPI } from '../services/api';
import RecruiterSidebar from '../components/RecruiterSidebar';
import { MEDIA_URL } from '../config';
import '../styles/theme.css';
import './SearchSeekers.css';

const PROVINCES = [
  'Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape',
  'Limpopo', 'Mpumalanga', 'North West', 'Free State', 'Northern Cape',
];

const WORK_MODES = ['Remote', 'Hybrid', 'On-site', 'Any'];

const EMPLOYMENT_STATUSES = [
  'Employed', 'Unemployed', 'Student', 'Open to opportunities',
];

export default function SearchSeekers() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const [q, setQ] = useState('');
  const [province, setProvince] = useState('');
  const [workMode, setWorkMode] = useState('');
  const [minExp, setMinExp] = useState('');
  const [maxExp, setMaxExp] = useState('');
  const [willingToRelocate, setWillingToRelocate] = useState('');
  const [employmentStatus, setEmploymentStatus] = useState('');

  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(0);
  const LIMIT = 20;

  const doSearch = useCallback(async (pageOverride = 0) => {
    setLoading(true);
    setSearched(true);
    try {
      const params = {
        skip: pageOverride * LIMIT,
        limit: LIMIT,
      };
      if (q.trim()) params.q = q.trim();
      if (province) params.province = province;
      if (workMode) params.work_mode = workMode;
      if (minExp !== '') params.min_experience = parseInt(minExp);
      if (maxExp !== '') params.max_experience = parseInt(maxExp);
      if (willingToRelocate !== '') params.willing_to_relocate = willingToRelocate === 'yes';
      if (employmentStatus) params.employment_status = employmentStatus;

      const res = await searchAPI.searchSeekers(params);
      setResults(res.data.results || []);
      setTotal(res.data.total || 0);
      setPage(pageOverride);
    } catch {
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, province, workMode, minExp, maxExp, willingToRelocate, employmentStatus]);

  // Load initial results on mount
  useEffect(() => {
    doSearch(0);
    // eslint-disable-line
  }, []); // intentional — only run once on mount

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') doSearch(0);
  };

  const clearFilters = () => {
    setProvince('');
    setWorkMode('');
    setMinExp('');
    setMaxExp('');
    setWillingToRelocate('');
    setEmploymentStatus('');
  };

  const hasFilters = province || workMode || minExp || maxExp || willingToRelocate || employmentStatus;
  const totalPages = Math.ceil(total / LIMIT);

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return `${MEDIA_URL}/${photo.replace(/^\/+/, '')}`;
  };

  const getWorkModeBadge = (mode) => {
    const map = { Remote: 'badge-green', Hybrid: 'badge-blue', 'On-site': 'badge-orange', Any: 'badge-gray' };
    return map[mode] || 'badge-gray';
  };

  return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Find Candidates</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme} title="Toggle theme">
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>

        <div className="portal-content">
          <div className="page-header">
            <h1>Find Candidates</h1>
            <p>Search job seekers by skills, location, experience, and more</p>
          </div>

          {/* Search bar + filters */}
          <div className="card ss-search-card">
            <div className="ss-search-row">
              <div className="ss-search-wrap">
                <i className="fas fa-search ss-search-icon"></i>
                <input
                  className="form-control ss-search-input"
                  type="text"
                  placeholder="Search by name, title, skills, or city..."
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                {q && (
                  <button className="ss-clear-btn" onClick={() => setQ('')} title="Clear">
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
              <button className="btn btn-primary ss-search-btn" onClick={() => doSearch(0)}>
                <i className="fas fa-search"></i> Search
              </button>
            </div>

            <div className="ss-filters-row">
              <select
                className="form-control ss-filter-select"
                value={province}
                onChange={e => setProvince(e.target.value)}
              >
                <option value="">All Provinces</option>
                {PROVINCES.map(p => <option key={p}>{p}</option>)}
              </select>

              <select
                className="form-control ss-filter-select"
                value={workMode}
                onChange={e => setWorkMode(e.target.value)}
              >
                <option value="">Any Work Mode</option>
                {WORK_MODES.map(m => <option key={m}>{m}</option>)}
              </select>

              <select
                className="form-control ss-filter-select"
                value={employmentStatus}
                onChange={e => setEmploymentStatus(e.target.value)}
              >
                <option value="">Any Status</option>
                {EMPLOYMENT_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>

              <div className="ss-exp-range">
                <input
                  className="form-control ss-exp-input"
                  type="number"
                  min="0"
                  max="50"
                  placeholder="Min yrs"
                  value={minExp}
                  onChange={e => setMinExp(e.target.value)}
                />
                <span className="ss-exp-sep">–</span>
                <input
                  className="form-control ss-exp-input"
                  type="number"
                  min="0"
                  max="50"
                  placeholder="Max yrs"
                  value={maxExp}
                  onChange={e => setMaxExp(e.target.value)}
                />
              </div>

              <select
                className="form-control ss-filter-select"
                value={willingToRelocate}
                onChange={e => setWillingToRelocate(e.target.value)}
              >
                <option value="">Relocation: Any</option>
                <option value="yes">Open to relocate</option>
                <option value="no">Not relocating</option>
              </select>

              {hasFilters && (
                <button className="btn btn-sm ss-clear-filters-btn" onClick={clearFilters}>
                  <i className="fas fa-times"></i> Clear filters
                </button>
              )}
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="empty-state">
              <i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i>
              <p>Searching candidates...</p>
            </div>
          ) : searched && results.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-users"></i>
              <h3>No candidates found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          ) : (
            <>
              {searched && (
                <p className="ss-results-count">
                  {total} {total === 1 ? 'candidate' : 'candidates'} found
                </p>
              )}

              <div className="ss-grid">
                {results.map(seeker => {
                  const photoUrl = getPhotoUrl(seeker.profile_photo);
                  return (
                    <div
                      key={seeker.id}
                      className="ss-card card"
                      onClick={() => navigate(`/recruiter/seeker-profile/${seeker.user_id}`)}
                    >
                      <div className="ss-card-top">
                        <div className="ss-avatar-wrap">
                          {photoUrl
                            ? <img src={photoUrl} alt={seeker.full_name} className="ss-avatar-img" />
                            : (
                              <div className="ss-avatar-placeholder">
                                <i className="fas fa-user"></i>
                              </div>
                            )
                          }
                        </div>
                        <div className="ss-card-meta">
                          <h3 className="ss-name">{seeker.full_name}</h3>
                          {seeker.professional_title && (
                            <p className="ss-title">{seeker.professional_title}</p>
                          )}
                          <div className="ss-meta-row">
                            {(seeker.city || seeker.province) && (
                              <span>
                                <i className="fas fa-map-marker-alt"></i>
                                {[seeker.city, seeker.province].filter(Boolean).join(', ')}
                              </span>
                            )}
                            {seeker.years_of_experience != null && (
                              <span>
                                <i className="fas fa-clock"></i>
                                {seeker.years_of_experience} yr{seeker.years_of_experience !== 1 ? 's' : ''} exp
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="ss-badges">
                        {seeker.work_mode_preference && (
                          <span className={`badge ${getWorkModeBadge(seeker.work_mode_preference)}`}>
                            <i className="fas fa-laptop-house"></i> {seeker.work_mode_preference}
                          </span>
                        )}
                        {seeker.employment_status && (
                          <span className="badge badge-gray">
                            {seeker.employment_status}
                          </span>
                        )}
                        {seeker.willing_to_relocate && (
                          <span className="badge badge-purple">
                            <i className="fas fa-plane"></i> Open to relocate
                          </span>
                        )}
                        {seeker.notice_period && (
                          <span className="badge badge-gray">
                            <i className="fas fa-calendar-check"></i> {seeker.notice_period}
                          </span>
                        )}
                      </div>

                      {/* Skills */}
                      {seeker.skills?.length > 0 && (
                        <div className="ss-skills">
                          {seeker.skills.slice(0, 5).map((s, i) => (
                            <span key={i} className="ss-skill-tag">
                              {typeof s === 'string' ? s : s.name}
                            </span>
                          ))}
                          {seeker.skills.length > 5 && (
                            <span className="ss-skill-tag ss-skill-more">
                              +{seeker.skills.length - 5}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Desired roles */}
                      {seeker.desired_job_titles?.length > 0 && (
                        <div className="ss-desired">
                          <i className="fas fa-bullseye"></i>
                          <span>{seeker.desired_job_titles.slice(0, 2).join(', ')}</span>
                        </div>
                      )}

                      {/* Salary expectation */}
                      {(seeker.salary_min || seeker.salary_max) && (
                        <div className="ss-salary">
                          <i className="fas fa-money-bill-wave"></i>
                          <span>
                            R{seeker.salary_min?.toLocaleString()} – R{seeker.salary_max?.toLocaleString()} / month
                          </span>
                        </div>
                      )}

                      <div className="ss-card-footer">
                        <div className="ss-social-links">
                          {seeker.linkedin_url && (
                            <a
                              href={seeker.linkedin_url}
                              target="_blank"
                              rel="noreferrer"
                              className="ss-social-btn"
                              onClick={e => e.stopPropagation()}
                              title="LinkedIn"
                            >
                              <i className="fab fa-linkedin"></i>
                            </a>
                          )}
                          {seeker.github_url && (
                            <a
                              href={seeker.github_url}
                              target="_blank"
                              rel="noreferrer"
                              className="ss-social-btn"
                              onClick={e => e.stopPropagation()}
                              title="GitHub"
                            >
                              <i className="fab fa-github"></i>
                            </a>
                          )}
                          {seeker.portfolio_url && (
                            <a
                              href={seeker.portfolio_url}
                              target="_blank"
                              rel="noreferrer"
                              className="ss-social-btn"
                              onClick={e => e.stopPropagation()}
                              title="Portfolio"
                            >
                              <i className="fas fa-globe"></i>
                            </a>
                          )}
                        </div>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={e => {
                            e.stopPropagation();
                            navigate(`/recruiter/seeker-profile/${seeker.user_id}`);
                          }}
                        >
                          View Profile <i className="fas fa-arrow-right"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="ss-pagination">
                  <button
                    className="btn btn-sm"
                    disabled={page === 0}
                    onClick={() => doSearch(page - 1)}
                  >
                    <i className="fas fa-chevron-left"></i> Previous
                  </button>
                  <span className="ss-page-info">Page {page + 1} of {totalPages}</span>
                  <button
                    className="btn btn-sm"
                    disabled={page >= totalPages - 1}
                    onClick={() => doSearch(page + 1)}
                  >
                    Next <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
